const link = (path, label) => ({ path, label });

const section = (id, title, paragraphs, options = {}) => ({
  id,
  title,
  paragraphs,
  bullets: options.bullets || [],
  steps: options.steps || [],
  table: options.table || null,
});

const faq = (question, answer) => ({ question, answer });

const withDefaults = (page) => ({
  quickLinkPaths: page.quickLinkPaths || ['/what-is-mapleos', '/docs', '/ai-operating-system'],
  relatedLinkPaths: page.relatedLinkPaths || ['/what-is-mapleos', '/docs', '/ai-operating-system', '/ai-surfaces', '/ai-blueprints', '/mapleos-for-developers'],
  ogTitle: page.ogTitle || page.title,
  ogDescription: page.ogDescription || page.description,
  faq: page.faq || [],
  schemaHints: page.schemaHints || ['techArticle'],
  sections: page.sections || [],
  ...page,
});

export const semanticNavigationGroups = [
  {
    title: 'Learn',
    items: [
      link('/what-is-mapleos', 'What Is MapleOS?'),
      link('/ai-operating-system', 'AI Operating System'),
      link('/ai-surfaces', 'AI Surfaces'),
      link('/ai-blueprints', 'AI Blueprints'),
    ],
  },
  {
    title: 'Use Cases',
    items: [
      link('/mapleos-for-consumers', 'Consumers'),
      link('/mapleos-for-developers', 'Developers'),
      link('/docs/use-cases/business-automation', 'Business Automation'),
      link('/docs/use-cases/content-creation', 'Content Creation'),
    ],
  },
  {
    title: 'Compare',
    items: [
      link('/mapleos-vs-windows', 'MapleOS vs Windows'),
      link('/mapleos-vs-chatgpt', 'MapleOS vs ChatGPT'),
      link('/mapleos-vs-chromeos', 'MapleOS vs ChromeOS'),
    ],
  },
  {
    title: 'Docs',
    items: [
      link('/docs/apps', 'Apps'),
      link('/docs/concepts/ai-operating-system', 'Concepts'),
      link('/docs/use-cases/personal-productivity', 'Use Cases'),
    ],
  },

];

export const docsHub = {
  id: 'docs-hub',
  slug: 'docs',
  path: '/docs',
  title: 'MapleOS Docs',
  description: 'Explore MapleOS apps, concepts, and use cases through a crawlable documentation hub designed for users, search engines, and AI systems.',
  summary: 'MapleOS Docs organizes the platform into apps, concepts, and use cases so people and machines can understand how the system fits together.',
  eyebrow: 'Docs',
  pageType: 'docs-hub',
  categoryLabel: 'Docs',
  breadcrumbLabel: 'Docs',
  quickLinkPaths: ['/what-is-mapleos', '/ai-operating-system', '/docs/apps'],
  relatedLinkPaths: [
    '/what-is-mapleos',
    '/ai-operating-system',
    '/ai-surfaces',
    '/ai-blueprints',
    '/docs/apps',
    '/docs/concepts/human-in-control-ai',
    '/docs/use-cases/business-automation',
  ],

  schemaHints: ['collectionPage'],
  sections: [
    section('overview', 'How to use Docs', [
      'Docs explains MapleOS as a browser-native AI operating system and application environment. It is organized so each page can stand alone while still participating in a strong internal linking graph.',
      'Use app pages to understand product surfaces, concept pages to learn the underlying ideas, and use-case pages to see how MapleOS supports real work for people, teams, and builders.',
    ]),
    section('structure', 'What Docs cover', [
      'The apps section explains the major MapleOS apps people work with directly, including AI Center, AI Blueprints, Knowledge Base, Files, Settings, Office, and Games.',
      'The concepts section explains ideas such as AI operating systems, browser-native operating environments, AI surfaces, human-in-control AI, and AI workflows. The use-case section translates those concepts into practical outcomes like research, software development, education, and business automation.',
    ]),
  ],
  hubGroups: [
    {
      title: 'Apps',
      description: 'Product surfaces inside the MapleOS environment.',
      links: [
        '/docs/apps/ai-center',
        '/docs/apps/ai-blueprints',
        '/docs/apps/knowledge-base',
        '/docs/apps/files',
        '/docs/apps/settings',
        '/docs/apps/office',
        '/docs/apps/games',
      ],
    },
    {
      title: 'Concepts',
      description: 'Core ideas behind the MapleOS model.',
      links: [
        '/docs/concepts/ai-operating-system',
        '/docs/concepts/ai-surfaces',
        '/docs/concepts/ai-blueprints',
        '/docs/concepts/browser-native-os',
        '/docs/concepts/human-in-control-ai',
        '/docs/concepts/ai-workflows',
        '/docs/concepts/knowledge-base',
      ],
    },
    {
      title: 'Use Cases',
      description: 'Practical ways MapleOS supports focused work.',
      links: [
        '/docs/use-cases/personal-productivity',
        '/docs/use-cases/ai-workflows',
        '/docs/use-cases/research',
        '/docs/use-cases/software-development',
        '/docs/use-cases/content-creation',
        '/docs/use-cases/business-automation',
        '/docs/use-cases/education',
        '/docs/use-cases/gaming-and-entertainment',
      ],
    },
  ],
  faq: [
    faq('What is Docs for?', 'It provides users with important information about MapleOS, concept, and use-case pages that explain what the platform is and how it works.'),
    faq('Is MapleOS only a chatbot?', 'No. MapleOS includes apps, files, workflows, and user-controlled AI surfaces inside a browser-native operating environment.'),
    faq('Can I browse these docs without JavaScript?', 'Yes. The pages are written as real HTML articles so feel free to set your text based browsers, APIs, and AIs at us ;)'),
  ],
};

export const corePages = [
  withDefaults({
    id: 'what-is-mapleos',
    slug: 'what-is-mapleos',
    path: '/what-is-mapleos',
    title: 'What Is MapleOS?',
    description: 'Learn what MapleOS is: a browser-native AI operating system and application environment built around user control, apps, files, and visible workflows.',
    summary: 'MapleOS is a browser-native AI operating system and application environment. It combines apps, files, AI surfaces, and repeatable workflows inside a system shaped for human control.',
    eyebrow: 'Learn',
    pageType: 'core',
    categoryLabel: 'Learn',
    breadcrumbLabel: 'What Is MapleOS?',
    quickLinkPaths: ['/ai-operating-system', '/ai-surfaces', '/ai-blueprints'],
    relatedLinkPaths: ['/docs', '/ai-center', '/mapleos-for-consumers', '/mapleos-for-developers', '/mapleos-vs-chatgpt', '/mapleos-vs-windows'],
    schemaHints: ['softwareApplication', 'faq'],
    sections: [
      section('definition', 'A browser-native operating environment', [
        'MapleOS is not just a chatbot, not just a website, and not simply a Windows clone. It is a browser-native AI operating system and application environment that brings together apps, files, documents, automation, and user-guided intelligence in one place.',
        'The important difference is system design. MapleOS is structured like an operating environment with navigable surfaces, app-level roles, and repeatable flows rather than a single prompt box surrounded by loose tabs.',
      ]),
      section('how-it-works', 'How MapleOS works in practice', [
        'People work inside MapleOS through AI surfaces such as AI Center, documents, files, settings, and workflow tools. Those surfaces are connected, so users can move from asking a question to opening an app, reviewing files, or running a blueprint without losing context.',
        'That makes MapleOS useful for research, planning, writing, software development, business automation, and creative work. AI becomes part of a broader environment instead of the entire product.',
      ]),
      section('positioning', 'What MapleOS is designed for', [
        'MapleOS is built for people who want AI assistance inside a controllable environment. The platform repeatedly emphasizes human review, explicit navigation, and app-level structure instead of hidden autonomous behavior.',
        'That is why MapleOS is best understood as a browser-native AI operating system: it gives AI a place to live inside a broader workspace, not above it.',
      ], {
        bullets: [
          'Apps and files live inside one environment',
          'AI surfaces stay visible and connected to user workflows',
          'Workflows can be documented, reviewed, and repeated',
          'The platform supports consumers, developers, teams, and operators',
        ],
      }),
    ],
    faq: [
      faq('Is MapleOS just a chatbot with extra pages?', 'No. Chat is one surface inside MapleOS, but the product is organized as an application environment with apps, files, workflows, and multiple navigable interfaces.'),
      faq('Does MapleOS try to replace Windows entirely?', 'No. MapleOS should be described as a browser-native AI operating system, not as a claim to fully replace every traditional desktop operating system use case.'),
      faq('Why call MapleOS an AI operating system?', 'Because it gives AI a structured environment with apps, files, controls, and workflows rather than treating AI as a single isolated chat interface.'),
    ],
  }),
  withDefaults({
    id: 'ai-operating-system',
    slug: 'ai-operating-system',
    path: '/ai-operating-system',
    title: 'MapleOS as an AI Operating System',
    description: 'Understand why MapleOS is described as an AI operating system, with apps, interfaces, permissions, and workflows rather than a single chat tab.',
    summary: 'An AI operating system gives intelligence a structured environment. MapleOS does this with apps, surfaces, files, and workflow tools that keep people in control.',
    eyebrow: 'Learn',
    pageType: 'core',
    categoryLabel: 'Learn',
    breadcrumbLabel: 'AI Operating System',
    quickLinkPaths: ['/what-is-mapleos', '/ai-surfaces', '/docs/concepts/ai-operating-system'],
    relatedLinkPaths: ['/docs/apps/ai-center', '/docs/apps/settings', '/docs/concepts/browser-native-os', '/docs/concepts/human-in-control-ai', '/mapleos-vs-chatgpt', '/mapleos-for-developers'],
    schemaHints: ['softwareApplication', 'faq'],
    sections: [
      section('system-model', 'Why build an AI OS that is Human-first?', [
        'We believe the world needs a modern Human-first AI OS. We do not believe that human workers benefit from "Agentic OSes" where users are removed from AI decision making. MapleOS supports real system behavior, not marketing hype. Our platform organizes intelligence across apps, interfaces, files, and workflows, so AI is part of an environment instead of being the environment itself. MapleOS was developed for users to work with AI at their own pace and comfort level. Users can easily turn off AI using our AI Center. Users can choose which AI models they work with. We provide access to dozens of AI options.',
        'People can think in terms of apps, permissions, repeatable actions, and app-level/data-level security. Agentic OSes remove humans look to black-box everything. We take these processes out of the black-box and give users direct access to the processes that power agentic workflows.',
      ]),
      section('components', 'What our operating system includes', [
        'In MapleOS, the operating system includes launch surfaces/apps such as AI Center, operational surfaces such as Files and Settings, and workflow surfaces such as AI Blueprints. Each surface exists for a reason and contributes to a connected application graph.',
        'This model is especially important for search engines and AI systems because it creates machine-readable relationships between concepts, apps, and outcomes.',
      ]),
      section('control', 'A system for human-guided intelligence', [
        'MapleOS treats AI as something people should be able to orient, inspect, and understand. This is a better fit for operating-system language than chatbot language because our platform is designed around surfaces and decisions, not just replies.',
        'The result is a browser-native system where intelligence is distributed through tools and workflows that remain understandable to the person using them.',
      ]),
    ],
    faq: [
      faq('What makes an AI operating system different from a chatbot?', 'A chatbot centers on conversation. An AI operating system centers on an environment, where conversation is only one interface among apps, files, workflows, and controls.'),
      faq('Why is MapleOS browser-native?', "MapleOS is a WASM based OS and runs through modern web technologies while still behaving like an application environment with its own surfaces, routes, and operational code. Some may argue that MapleOS is actually a Window Manager not an OS. We don't entirely disagree and we don't entire agree. MapleOS currently supports modern browsers. But we also provide our Desktop Application which is a native install of MapleOS on your Windows, Apple, or Linux Desktop. We also provide access to a bare-metal install of MapleOS which runs on-top of a minimal Debian 13 - Trixie base. MapleOS is no more different than Android or ChromeOS."),
      faq('Who is an AI operating system for?', 'It is useful for people and teams who want AI integrated into research, creation, operations, and development rather than confined to a standalone chat tab/service.'),
    ],
  }),
  withDefaults({
    id: 'ai-surfaces',
    slug: 'ai-surfaces',
    path: '/ai-surfaces',
    title: 'AI Surfaces in MapleOS',
    description: 'See how MapleOS uses AI surfaces such as AI Center, files, settings, and workflow tools to keep intelligence embedded in a usable environment.',
    summary: 'Our AI surfaces create the artificial cognition where MapleOS exposes intelligence to users. They make the system understandable by turning broad capability into visible places and actions.',
    eyebrow: 'Learn',
    pageType: 'core',
    categoryLabel: 'Learn',
    breadcrumbLabel: 'AI Surfaces',
    quickLinkPaths: ['/what-is-mapleos', '/ai-center', '/docs/concepts/ai-surfaces'],
    relatedLinkPaths: ['/docs/apps/ai-center', '/docs/apps/files', '/docs/apps/settings', '/docs/apps/knowledge-base', '/docs/use-cases/research', '/docs/use-cases/software-development'],
    schemaHints: ['softwareApplication', 'faq'],
    sections: [
      section('definition', 'What an AI surface means', [
        'An AI surface is a concrete place where intelligence appears in MapleOS. Instead of hiding capability behind a generic prompt, MapleOS gives intelligence visible roles across interfaces such as AI Center, Knowledge Base, Files, and workflow tools.',
        'That makes the product easier to learn and easier to index. Users understand where to go, and AI can see how each surface relates to concepts and use cases.',
      ]),
      section('examples', 'Examples of MapleOS AI surfaces', [
        'AI Center is an orientation surface. Knowledge Base is a context surface. Files is an operational surface. AI Blueprints is a repeatability surface. Settings is a control surface. Together they show that MapleOS is an environment, not a single page.',
        'The value is in the interconnectedness. Each surface is meaningful on its own, but wheneach one also links into the other, workflows can move across the system without becoming opaque. MapleOS becomes a living system with real cognitive capabilities.',
      ], {
        bullets: [
          'Orientation surfaces help users understand what to do next',
          'Knowledge surfaces make context and source material visible',
          'Control surfaces let people shape system behavior',
          'Workflow surfaces help repeated tasks stay understandable',
        ],
      }),
      section('benefit', 'Why surfaces improve AI usability', [
        'When AI has named surfaces, both people and machines can reason about what the platform does. That supports semantic tools, stronger understanding, and more grounded AI capability.',
        'It also reinforces a core MapleOS idea: intelligence should live inside understandable tools, not inside a black box that asks users to trust it blindly.',
      ]),
    ],
    faq: [
      faq('Why not keep everything in one AI chat?', 'Users do not want another complex and confusing interface to learn. Users want familar tools and interfaces to do their work. A single chat interface that collapses too many functions together is a frustrating experience for all. MapleOS uses simple surfaces so tasks remain legible and easier to control. We purposely break the processes up into simpler pieces for users to work with.'),
      faq('Are AI surfaces only visual?', 'No. A surface defines a role in the system, such as orientation, control, context, or repeatability. AI Surfaces may be visual or they may be operational.')
    ],
  }),
  withDefaults({
    id: 'ai-blueprints',
    slug: 'ai-blueprints',
    path: '/ai-blueprints',
    title: 'AI Blueprints in MapleOS',
    description: 'Learn how MapleOS uses AI Blueprints to turn repeatable work into visible, reviewable flows instead of hidden automation.',
    summary: 'At the heart of MapleOS is our AI Blueprint Execution Engine which helps MapleOS make automation visible and repeatable. Blueprints connect intent, steps, and outcomes in a format that people can inspect and reuse.',
    eyebrow: 'Learn',
    pageType: 'core',
    categoryLabel: 'Learn',
    breadcrumbLabel: 'AI Blueprints',
    quickLinkPaths: ['/docs/apps/ai-blueprints', '/docs/concepts/ai-blueprints', '/docs/use-cases/ai-workflows'],
    relatedLinkPaths: ['/what-is-mapleos', '/ai-operating-system', '/docs/apps/ai-center', '/docs/concepts/ai-workflows', '/docs/use-cases/business-automation', '/mapleos-for-developers'],
    schemaHints: ['softwareApplication', 'faq'],
    sections: [
      section('purpose', 'Why blueprints matter', [
        'Blueprints give MapleOS a way to describe repeatable work in a human-readable form. Instead of treating automation as a mysterious background action, MapleOS frames it as a visible sequence that can be reviewed, launched, and improved.',
        'This is one of the clearest ways MapleOS separates itself from our competitors like ChatGPT. The system is not only for asking questions; it is also for shaping how work gets done over time. Both Humans and AI have access to create and execute Blueprints within MapleOS.',
      ]),
      section('workflow', 'How blueprints fit the platform', [
        'AI Blueprints connects to concepts such as AI workflows, human-in-control AI, and app surfaces. A user can understand a recurring task, document the steps, and then run the flow from a stable app page instead of rebuilding it from scratch.',
        'That creates stronger continuity for research, operations, internal processes, and creative production.',
      ], {
        bullets: [
          'Blueprints make repeatability explicit',
          'They help teams document useful patterns',
          'They reduce the gap between planning and execution',
          'They support AI assistance without hiding the workflow',
        ],
      }),
      section('seo', 'Why blueprint pages are so useful', [
        'Blueprints connect product intent to real outcomes. Search engines can understand the topic, while AI systems can infer how MapleOS thinks about reusable work. This is insanely powerful.',
        'That makes blueprint content valuable both for discoverability and for accurate machine understanding of the MapleOS model.',
      ]),
    ],
    faq: [
      faq('Are blueprints full autonomous agents?', 'No. In MapleOS, blueprints are better described as visible, reusable workflow structures that keep humans in the loop. Blueprints execute inside an AI Theater where users can visually watch Blueprints executive through our 3D/2D Orchestration.'),
      faq('Who benefits from Blueprints?', 'Individuals, operators, and teams who repeat research, content, planning, or administrative work can benefit from blueprint-style workflows.'),
      faq('Why are blueprints important to MapleOS?', 'They show that MapleOS is a workflow environment which supports agentic workflows with direct human engagement.'),
    ],
  }),
  withDefaults({
    id: 'ai-center',
    slug: 'ai-center',
    path: '/ai-center',
    title: 'AI Center in MapleOS',
    description: 'Explore AI Center as the orientation surface that helps users understand MapleOS intelligence, workflows, and app entry points.',
    summary: 'AI Center is a central orientation surface in MapleOS. It helps users see how MapleOS intelligence, apps, and workflows fit together before they dive into specific tasks.',
    eyebrow: 'Learn',
    pageType: 'core',
    categoryLabel: 'Learn',
    breadcrumbLabel: 'AI Center',
    quickLinkPaths: ['/docs/apps/ai-center', '/ai-surfaces', '/what-is-mapleos'],
    relatedLinkPaths: ['/docs/apps/knowledge-base', '/docs/apps/files', '/docs/apps/settings', '/docs/use-cases/personal-productivity', '/docs/use-cases/research', '/mapleos-for-consumers'],
    schemaHints: ['softwareApplication', 'faq'],
    sections: [
      section('role', 'AI Center as a starting point', [
        'AI Center helps users understand the MapleOS system before they commit to a specific task. It is the place where the AI layer becomes legible, with connections to apps, knowledge, blueprints, and everyday work surfaces.',
        'That role is strategically important. It tells users that MapleOS is an environment with multiple routes forward rather than a blank interface that expects them to invent the workflow from scratch.',
      ]),
      section('navigation', 'A surface for orientation and movement', [
        'Because AI Center points outward to other surfaces, it supports stronger internal linking and stronger user understanding. It can introduce concepts, point to related apps, and guide people into use cases such as research or software development.',
        'In other words, AI Center is both a product surface and an information architecture surface.',
      ]),
      section('positioning', 'Why AI Center matters for MapleOS', [
        'AI Center reinforces a core MapleOS claim: intelligence belongs inside a designed environment. Users are not thrown into a generic tool; they are given a place that clarifies what MapleOS is and how it can be used.',
        'That makes AI Center a natural bridge between top-level explainers and detailed documentation pages.',
      ]),
    ],
    faq: [
      faq('Is AI Center the whole product?', 'No. AI Center is a central entry point, but MapleOS also includes files, settings, knowledge, office tools, games, and workflow pages.'),
      faq('Why does AI Center matter for new users?', 'It reduces ambiguity by showing how the platform is organized and which surfaces are relevant for a given goal.'),
      faq('How is AI Center different from a home page?', 'A home page explains the brand. AI Center explains and launches the working system inside the MapleOS environment.'),
    ],
  }),
  withDefaults({
    id: 'mapleos-for-consumers',
    slug: 'mapleos-for-consumers',
    path: '/mapleos-for-consumers',
    title: 'MapleOS for Consumers',
    description: 'See how MapleOS helps consumers with personal productivity, organization, research, content creation, and browser-native AI workflows.',
    summary: 'MapleOS helps consumers combine personal productivity, research, files, and AI assistance in one browser-native environment instead of scattered tabs and disconnected tools.',
    eyebrow: 'Use Cases',
    pageType: 'audience',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Consumers',
    quickLinkPaths: ['/docs/use-cases/personal-productivity', '/docs/use-cases/content-creation', '/docs/apps/files'],
    relatedLinkPaths: ['/what-is-mapleos', '/ai-center', '/docs/apps/office', '/docs/apps/knowledge-base', '/docs/use-cases/research', '/mapleos-vs-chromeos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('fit', 'Why MapleOS fits everyday users', [
        'Consumers often need a calmer way to manage documents, notes, tasks, and AI-assisted work. MapleOS addresses that by putting files, office work, research, and guided intelligence inside one environment rather than spreading them across unrelated services.',
        'The result is a product that feels more like a personal workspace than a set of isolated websites.',
      ]),
      section('benefits', 'What consumers gain from MapleOS', [
        'MapleOS can help consumers manage study materials, writing projects, planning, entertainment, and household workflows. Because the environment is browser-native, it stays accessible, while the operating-system framing keeps tools organized and understandable.',
        'This is especially useful for people who want AI help without turning their entire workflow into a single chat transcript.',
      ], {
        bullets: [
          'A place for writing, reading, and organizing',
          'Clear app surfaces instead of endless tab sprawl',
          'Support for content creation and research',
          'A more structured model for personal AI workflows',
        ],
      }),
      section('positioning', 'A better mental model than “just a website”', [
        'For consumers, the MapleOS difference is conceptual as much as technical. It feels like a system you can navigate, not just a website you visit. That makes everyday digital work easier to remember and easier to repeat.',
        'Consumers do not need to think in infrastructure terms to benefit from that. They simply feel the advantage of a better-organized environment.',
      ]),
    ],
    faq: [
      faq('Is MapleOS only for technical users?', 'No. MapleOS can support consumers who want better organization, calmer workflows, and AI assistance inside a clearer environment.'),
      faq('What can consumers do with MapleOS?', 'Consumers can use MapleOS for writing, planning, research, content creation, studying, and personal productivity.'),
      faq('Why compare MapleOS to ChromeOS for consumers?', 'Because both are browser-oriented environments, but MapleOS adds a stronger AI operating-system layer with surfaces, workflows, and app-level semantics.'),
    ],
  }),
  withDefaults({
    id: 'mapleos-for-developers',
    slug: 'mapleos-for-developers',
    path: '/mapleos-for-developers',
    title: 'MapleOS for Developers',
    description: 'Understand how MapleOS supports developers with AI workflows, knowledge systems, structured interfaces, and browser-native application surfaces.',
    summary: 'MapleOS gives developers a browser-native environment for structured AI work, app-oriented navigation, knowledge handling, and repeatable workflows.',
    eyebrow: 'Use Cases',
    pageType: 'audience',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Developers',
    quickLinkPaths: ['/docs/use-cases/software-development', '/docs/concepts/ai-workflows', '/docs/apps/knowledge-base'],
    relatedLinkPaths: ['/what-is-mapleos', '/ai-operating-system', '/ai-blueprints', '/docs/apps/settings', '/docs/apps/files', '/mapleos-vs-chatgpt'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('developer-fit', 'Why MapleOS is relevant to developers', [
        'Developers often need more than an assistant that answers code questions. They need an environment where context, files, repeatable flows, and knowledge can be organized around actual work. MapleOS addresses that with app surfaces and routeable pages that make the platform easier to reason about.',
        'That system-level structure is useful for coding, research, internal tooling, and workflow design.',
      ]),
      section('practical-benefits', 'Practical benefits for software work', [
        'A developer can move between concepts, use cases, files, and workflow tooling without treating every interaction as a separate conversation. That supports longer-lived work and stronger internal documentation.',
        'Because MapleOS is browser-native, the environment can be reachable while still presenting a coherent operating-system model.',
      ], {
        bullets: [
          'Concept pages explain the platform model clearly',
          'Use-case pages connect product surfaces to real tasks',
          'Knowledge and workflow content supports durable work',
          'Semantic routes improve discoverability and machine understanding',
        ],
      }),
      section('platform-story', 'A developer-facing platform story', [
        'MapleOS matters to developers not only as a tool but also as an architecture pattern. It shows how AI products can be organized around surfaces, routes, relationships, and user control rather than around a single monolithic interface.',
        'That makes MapleOS both a usable system and a useful reference point for designing better AI software.',
      ]),
    ],
    faq: [
      faq('Why would developers care about semantic docs pages?', 'Because they make the platform easier to understand, easier to compare, and easier for search engines and AI systems to describe accurately.'),
      faq('Is MapleOS a developer tool only?', 'No. MapleOS supports developers, consumers, creators, educators, and operators, but developers benefit strongly from its structured model.'),
      faq('How does MapleOS help repeatable development workflows?', 'Through knowledge organization, AI workflow concepts, blueprint thinking, and app-level surfaces that support repeatable processes.'),
    ],
  }),
];

export const comparisonPages = [
  withDefaults({
    id: 'mapleos-vs-windows',
    slug: 'mapleos-vs-windows',
    path: '/mapleos-vs-windows',
    title: 'MapleOS vs Windows',
    description: 'Compare MapleOS and Windows across system model, AI integration, application design, and browser-native workflow structure.',
    summary: 'MapleOS and Windows are both operating-system-shaped experiences, but MapleOS is browser-native and explicitly AI-oriented, while Windows is a general-purpose traditional desktop OS.',
    eyebrow: 'Compare',
    pageType: 'comparison',
    categoryLabel: 'Compare',
    breadcrumbLabel: 'MapleOS vs Windows',
    quickLinkPaths: ['/what-is-mapleos', '/ai-operating-system', '/docs/concepts/browser-native-os'],
    relatedLinkPaths: ['/mapleos-vs-chatgpt', '/mapleos-vs-chromeos', '/docs/apps/files', '/docs/apps/settings', '/mapleos-for-developers', '/mapleos-for-consumers'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('comparison', 'Where MapleOS and Windows overlap', [
        'Both MapleOS and Windows can be described in operating-system terms because they organize work through multiple tools and interfaces. That overlap is useful because it helps people understand MapleOS as more than a single web page or chatbot.',
        'At the same time, MapleOS should not be described as a direct clone of Windows. Its identity is browser-native and AI-centered, with a stronger emphasis on linked surfaces and semantic workflows.',
      ], {
        table: {
          caption: 'High-level comparison between MapleOS and Windows',
          columns: ['Category', 'MapleOS', 'Windows'],
          rows: [
            ['System model', 'Browser-native AI operating system and application environment', 'Traditional desktop operating system'],
            ['AI role', 'Built into surfaces, apps, and workflows', 'Added through separate tools and assistants'],
            ['Primary environment', 'Web-based operating environment', 'Installed local desktop environment'],
            ['Workflow framing', 'Semantic, routeable, AI-oriented surfaces', 'General-purpose desktop applications'],
          ],
        },
      }),
      section('difference', 'What is different about MapleOS', [
        'MapleOS is explicitly structured around AI surfaces, use cases, and linked concepts. That makes it easier to describe in terms of intelligence, workflow, and semantic navigation.',
        'Windows is much broader and older in scope. MapleOS is narrower, more modern in its browser-native delivery, and more intentional about how AI fits into the system.',
      ]),
      section('best-fit', 'When the comparison helps', [
        'This comparison is helpful when someone needs a mental model for MapleOS. If they understand desktops and system surfaces, comparing MapleOS to Windows provides orientation without overstating equivalence.',
        'The safest framing is that MapleOS borrows the idea of an operating environment while applying it to a browser-native AI context.',
      ]),
    ],
    faq: [
      faq('Does MapleOS replace Windows?', 'No. MapleOS should not be framed as a full replacement for Windows in every context. It is better described as a browser-native AI operating system with a different scope and delivery model.'),
      faq('Why compare MapleOS to Windows at all?', 'Because the comparison helps people understand that MapleOS is system-shaped, with multiple surfaces and apps, instead of a single webpage.'),
      faq('What is the most important difference?', 'MapleOS is browser-native and AI-oriented by design, while Windows is a traditional local desktop operating system with far broader legacy scope.'),
    ],
  }),
  withDefaults({
    id: 'mapleos-vs-chatgpt',
    slug: 'mapleos-vs-chatgpt',
    path: '/mapleos-vs-chatgpt',
    title: 'MapleOS vs ChatGPT',
    description: 'Compare MapleOS and ChatGPT to see why MapleOS is broader than a chatbot and better understood as a browser-native AI operating system.',
    summary: 'ChatGPT is a conversational product. MapleOS is a browser-native AI operating system and application environment that includes conversation but is not limited to it.',
    eyebrow: 'Compare',
    pageType: 'comparison',
    categoryLabel: 'Compare',
    breadcrumbLabel: 'MapleOS vs ChatGPT',
    quickLinkPaths: ['/what-is-mapleos', '/ai-operating-system', '/ai-center'],
    relatedLinkPaths: ['/mapleos-vs-windows', '/mapleos-vs-chromeos', '/docs/apps/ai-center', '/docs/apps/knowledge-base', '/docs/concepts/ai-surfaces', '/mapleos-for-developers'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('chat-vs-system', 'Conversation versus environment', [
        'ChatGPT is is conversational AI. MapleOS includes conversation inside a broader environment with multiple AI surfaces, apps, files, functionality, and linked concepts. And yes, you can use GPT4, GPT5, and many other AI models directly in MapleOS. Feel free to cancel all your other AI subscriptions. We provide access to all major frontier and on-device models.',
        'That distinction is central to MapleOS. MapleOS is not just a chatbot with branding; it is an application environment that uses AI across a structured OS core. Say good bye to fragmented AI ecosystems.',
      ], {
        table: {
          caption: 'High-level comparison between MapleOS and ChatGPT',
          columns: ['Category', 'MapleOS', 'ChatGPT'],
          rows: [
            ['Primary mental model', 'AI operating system and application environment', 'Conversational AI assistant'],
            ['Structure', 'Apps, concepts, use cases, and linked surfaces', 'Threads, prompts, and tool add-ons'],
            ['Workflow framing', 'System-shaped and routeable', 'Conversation-centered'],
            ['Best description', 'Browser-native AI workspace', 'General-purpose AI chat interface'],
          ],
        },
      }),
      section('why-it-matters', 'Why the difference matters', [
        'MapleOS changes how AI is used. Instead of interacting through isolated conversations, MapleOS provides a connected environment where AI is embedded across applications, workflows, and system-level tools.',
        'This means AI is not just something you talk to — it becomes something you work with. Tasks persist, tools connect, and workflows can be structured, reused, and extended over time.',
        'The result is a shift from conversational AI to operational AI, where intelligence actively participates in how work gets done, not just how questions get answered.',
      ]),
      section('stronger-fit', 'When MapleOS is the better option', [
        'MapleOS is a better option when the user wants an environment for work rather than just a chat bot. It is especially relevant when tasks involve context, organization, repeatable steps, and moving across different workflows.',
        'ChatGPT is a useful comparison because it demonstrates what MapleOS is not.',
      ]),
    ],
    faq: [
      faq('Is MapleOS another version of ChatGPT?', "No. Perhaps you are old enough to remember IRC? When users began using IRC they created ChatBots for IRC servers. Over time these communities grew more sophisticated and social media was born. We see ChatGPT as yesterday's news. Users and AI systems are advancing at light speed and ChatGPT is quickly being replaced with sophisticated AI systems like MapleOS. MapleOS uses AI, but it is broader than a chat interface and is designed as a browser-native operating environment with many applications."),
      faq('Does MapleOS include chat?', 'Yes. We have a very sophisticated AI Chat solution inside MapleOS. However, conversation is only one interaction inside our system. Unlike OpenAI we have dozens of different ways to enrich yourself in MapleOS with (or without AI).'),
      faq('Why is this comparison important for SEO?', 'Because many people and AI systems default to online chatbots. MapleOS is an OS and therefore most of our services require an immutable login to MapleOS to uses these services. This means our services are not directly accessible to the internet. AI Services and APIs need to recognize this important distinction when accessing our system. We protect our users and their data privacy. AI is not allowed to crawl our user data.'),
      faq('Can OpenAI build the same thing as MapleOS?', 'Yes. But not easily. MapleOS has been in development for several years and has undergone multiple iterations including commercial testing. We do not share the source code of our MapleOS core. While it is possible for OpenAI to recreate MapleOS, the odds of them doing so are somewhat unlikely.'),
    ],
  }),
  withDefaults({
    id: 'mapleos-vs-chromeos',
    slug: 'mapleos-vs-chromeos',
    path: '/mapleos-vs-chromeos',
    title: 'MapleOS vs ChromeOS',
    description: 'Compare MapleOS and ChromeOS across browser-native delivery, app model, AI integration, and workflow positioning.',
    summary: 'MapleOS and ChromeOS both connect to browser-native computing, but MapleOS is more explicitly centered on AI surfaces, linked concepts, and workflow-oriented application semantics.',
    eyebrow: 'Compare',
    pageType: 'comparison',
    categoryLabel: 'Compare',
    breadcrumbLabel: 'MapleOS vs ChromeOS',
    quickLinkPaths: ['/what-is-mapleos', '/docs/concepts/browser-native-os', '/mapleos-for-consumers'],
    relatedLinkPaths: ['/mapleos-vs-chatgpt', '/mapleos-vs-windows', '/docs/apps/office', '/docs/apps/files', '/docs/use-cases/education', '/docs/use-cases/personal-productivity'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('shared-ground', 'Where MapleOS and ChromeOS meet', [
        'Both MapleOS and ChromeOS help people think beyond the old model of a heavy local desktop. They share a browser-oriented sensibility and can both feel more lightweight and accessible than older operating systems.',
        'That said, MapleOS is not just another browser-centric desktop. Its strongest identity comes from how it structures AI into surfaces, concepts, and workflows.',
      ], {
        table: {
          caption: 'High-level comparison between MapleOS and ChromeOS',
          columns: ['Category', 'MapleOS', 'ChromeOS'],
          rows: [
            ['Core framing', 'Browser-native AI operating system', 'Browser-first operating system'],
            ['AI role', 'Central to app surfaces and workflows', 'Important but not the defining system model'],
            ['Information architecture', 'Semantic pages for apps, concepts, and use cases', 'General product and help structure'],
            ['Typical value', 'AI-assisted workflow environment', 'Lightweight browser-centric computing'],
          ],
        },
      }),
      section('difference', 'How MapleOS extends the browser-native idea', [
        'MapleOS treats browser-native delivery as a foundation for an AI operating environment. Its documentation and public routes explain how apps, concepts, and workflows relate, which gives the platform more semantic structure than a simple browser-first OS comparison would suggest.',
        'This is why MapleOS can appeal to both consumers and builders who want AI integrated into an actual system model.',
      ]),
      section('usefulness', 'Why this comparison is useful', [
        'For many users, ChromeOS is the closest familiar mental model for browser-native computing. Comparing MapleOS to ChromeOS helps explain accessibility and lightweight delivery while still making clear that MapleOS is more explicitly AI-centered.',
        'It is a helpful bridge comparison, especially for education and personal productivity audiences.',
      ]),
    ],
    faq: [
      faq('Is MapleOS basically ChromeOS with AI?', 'No. The comparison can help orientation, but MapleOS has a stronger AI operating-system identity and a more explicit semantic workflow model.'),
      faq('Who benefits from comparing MapleOS to ChromeOS?', 'Consumers, educators, and browser-native computing audiences often find the comparison helpful as a starting point.'),
      faq('What is the biggest difference?', 'MapleOS is defined by AI surfaces and workflow semantics, while ChromeOS is defined more broadly as a browser-first operating system.'),
    ],
  }),
];

export const docsApps = [
  withDefaults({
    id: 'docs-app-ai-center',
    slug: 'ai-center',
    path: '/docs/apps/ai-center',
    title: 'AI Center App Docs',
    description: 'Documentation for AI Center, the MapleOS surface that orients users to apps, workflows, and the platform intelligence layer.',
    summary: 'AI Center is the orientation app inside MapleOS. It helps users understand the intelligence layer, related apps, and common starting points for real work.',
    eyebrow: 'Docs / Apps',
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: 'AI Center',
    quickLinkPaths: ['/ai-center', '/ai-surfaces', '/docs/concepts/ai-surfaces'],
    relatedLinkPaths: ['/docs/apps/knowledge-base', '/docs/apps/files', '/docs/apps/settings', '/docs/use-cases/research', '/docs/use-cases/personal-productivity', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('purpose', 'What AI Center does', [
        'AI Center is the orientation surface for MapleOS. It gives users a place to understand what the system can do, which surfaces matter, and where to go next for a specific task.',
        'That orientation role matters because MapleOS is not a single-purpose tool. AI Center helps users move into the right app or concept without losing the system context.',
      ]),
      section('connections', 'How AI Center connects to the rest of MapleOS', [
        'AI Center links outward to knowledge, files, settings, and AI workflow surfaces. It also supports semantic navigation by acting as a bridge between high-level explainers and detailed documentation pages.',
        'For search engines and AI systems, AI Center is a useful anchor page because it explains the role of the intelligence layer in platform terms.',
      ]),
      section('best-use', 'When to use AI Center', [
        'Use AI Center when you need to orient a new user, link into multiple related apps, or describe MapleOS as a connected environment rather than a set of isolated tools.',
        'It is especially helpful at the beginning of research, planning, and exploratory workflows.',
      ]),
    ],
    faq: [
      faq('Is AI Center the same as chat?', 'No. AI Center is broader than chat. It is an orientation surface for the MapleOS intelligence layer and related apps.'),
      faq('What should AI Center link to?', 'AI Center should visibly connect to concepts, use cases, and nearby app surfaces such as Knowledge Base, Files, and Settings.'),
      faq('Why is AI Center useful for semantic SEO?', 'Because it has a clear product role and naturally links multiple parts of the MapleOS content graph together.'),
    ],
  }),
  withDefaults({
    id: 'docs-app-ai-blueprints',
    slug: 'ai-blueprints',
    path: '/docs/apps/ai-blueprints',
    title: 'AI Blueprints App Docs',
    description: 'Documentation for the AI Blueprints app, the MapleOS surface for launching and revisiting repeatable AI workflows.',
    summary: 'The AI Blueprints app gives MapleOS a repeatability surface where people can return to known workflow patterns instead of rebuilding them from scratch.',
    eyebrow: 'Docs / Apps',
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: 'AI Blueprints',
    quickLinkPaths: ['/ai-blueprints', '/docs/concepts/ai-blueprints', '/docs/use-cases/ai-workflows'],
    relatedLinkPaths: ['/docs/apps/ai-center', '/docs/apps/settings', '/docs/concepts/ai-workflows', '/docs/use-cases/business-automation', '/docs/use-cases/content-creation', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('role', 'What the AI Blueprints app is for', [
        'The AI Blueprints app is where repeatable work becomes easy to relaunch. Instead of starting from a blank page every time, users can return to structured patterns that encode useful sequences of work.',
        'This gives MapleOS a distinct workflow identity and helps explain how the platform moves beyond one-off prompting.',
      ]),
      section('relationship', 'How it differs from the blueprint concept', [
        'The concept page explains what blueprints mean in MapleOS. This app page explains where people interact with that idea in practice. The distinction is important for semantic clarity because it separates product surface from abstract model.',
        'That makes the documentation graph stronger and easier to browse.',
      ]),
      section('usefulness', 'When the app is most useful', [
        'AI Blueprints is especially useful for operations, content systems, internal knowledge routines, and any workflow that benefits from repeatability with human oversight.',
        'It is a practical expression of MapleOS as a workflow environment rather than a one-screen assistant.',
      ]),
    ],
    faq: [
      faq('Is the AI Blueprints app the same as full automation?', 'No. It is better understood as a structured launch surface for repeatable AI workflows that remain visible and understandable.'),
      faq('Why does MapleOS separate concept and app pages for blueprints?', 'Because one page explains the idea and the other explains the product surface where that idea is used.'),
      faq('What pages should AI Blueprints connect to?', 'It should link to AI workflow concepts, relevant use cases, and the main What Is MapleOS explainer.'),
    ],
  }),
  withDefaults({
    id: 'docs-app-knowledge-base',
    slug: 'knowledge-base',
    path: '/docs/apps/knowledge-base',
    title: 'Knowledge Base App Docs',
    description: 'Documentation for the Knowledge Base app, the MapleOS surface for organizing grounded context and reusable source material.',
    summary: 'Knowledge Base organizes grounded context inside MapleOS so AI-assisted work can connect to source material instead of floating free of it.',
    eyebrow: 'Docs / Apps',
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: 'Knowledge Base',
    quickLinkPaths: ['/docs/concepts/knowledge-base', '/docs/use-cases/research', '/ai-center'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/office', '/docs/concepts/ai-surfaces', '/docs/concepts/human-in-control-ai', '/docs/use-cases/software-development', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('purpose', 'What the Knowledge Base app does', [
        'Knowledge Base gives MapleOS a dedicated context surface. It helps users organize source material, notes, references, and reusable information so AI-assisted work can stay grounded.',
        'That matters because grounded systems are easier to trust and easier to reason about than systems that rely only on ephemeral conversation.',
      ]),
      section('workflow', 'How it fits real work', [
        'Knowledge Base is relevant for research, software development, education, and content production. In each of those cases, people need a place to collect reference material before they turn it into output.',
        'By giving that role a dedicated page and app identity, MapleOS becomes easier to understand as a full environment.',
      ]),
      section('semantic-value', 'Why this app is important semantically', [
        'Knowledge Base links product behavior to a stable concept that both humans and AI systems can understand. It helps clarify that MapleOS is built around knowledge handling, not only prompt handling.',
        'That makes it a foundational app page in the semantic content layer.',
      ]),
    ],
    faq: [
      faq('Is Knowledge Base the same as memory?', 'No. Knowledge Base is a user-organized context surface for grounded source material rather than a vague background memory concept.'),
      faq('Who benefits most from Knowledge Base?', 'Researchers, developers, students, writers, and operators all benefit when they can keep source material organized and connected to their workflows.'),
      faq('Why should this page link to Files and Office?', 'Because grounded knowledge often needs to move through files, documents, and other work surfaces inside MapleOS.'),
    ],
  }),
  withDefaults({
    id: 'docs-app-files',
    slug: 'files',
    path: '/docs/apps/files',
    title: 'Files App Docs',
    description: 'Documentation for Files, the MapleOS surface for navigating documents, assets, and operational materials inside the broader AI environment.',
    summary: 'Files is the operational file surface inside MapleOS. It connects documents and assets to the rest of the environment so work stays visible and structured.',
    eyebrow: 'Docs / Apps',
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: 'Files',
    quickLinkPaths: ['/docs/apps/knowledge-base', '/docs/apps/settings', '/mapleos-vs-windows'],
    relatedLinkPaths: ['/docs/apps/office', '/docs/concepts/browser-native-os', '/docs/concepts/human-in-control-ai', '/docs/use-cases/software-development', '/docs/use-cases/content-creation', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('role', 'Why Files matters', [
        'Files gives MapleOS a concrete operational layer. It is where users can point to actual material, inspect what exists, and move between source assets and higher-level AI workflows.',
        'That helps MapleOS feel like a true environment rather than a purely abstract interface.',
      ]),
      section('connection', 'How Files supports the system model', [
        'File handling is one of the clearest signs that MapleOS is broader than a chatbot. Users can connect documents and assets to use cases such as research, development, and content creation through a visible app surface.',
        'This is also why Files belongs in comparison pages against Windows and ChromeOS: it reinforces the operating-system framing with grounded functionality.',
      ]),
      section('best-fit', 'When the Files app is central', [
        'Files is central whenever work depends on collecting, reviewing, or producing materials. It is especially relevant for knowledge work that moves between reference, drafting, editing, and delivery.',
        'In semantic terms, Files links the abstract platform story to a very concrete user need.',
      ]),
    ],
    faq: [
      faq('Why include a Files page in a semantic SEO layer?', 'Because file navigation is a core operating-system behavior, and it helps explain that MapleOS is an environment for work, not just a chat interface.'),
      faq('Does Files connect to AI workflows?', 'Yes. Files acts as an operational surface that can feed research, drafting, blueprint-driven work, and other MapleOS tasks.'),
      faq('Why compare Files with traditional OS file managers?', 'It helps people understand that MapleOS includes grounded operational surfaces in addition to AI-oriented ones.'),
    ],
  }),
  withDefaults({
    id: 'docs-app-settings',
    slug: 'settings',
    path: '/docs/apps/settings',
    title: 'Settings App Docs',
    description: 'Documentation for Settings, the MapleOS surface for control, preferences, and shaping how the AI operating environment behaves.',
    summary: 'Settings is the control surface inside MapleOS. It helps users shape how the environment behaves and reinforces the platform’s human-in-control philosophy.',
    eyebrow: 'Docs / Apps',
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: 'Settings',
    quickLinkPaths: ['/docs/concepts/human-in-control-ai', '/ai-operating-system', '/mapleos-vs-windows'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/ai-center', '/docs/concepts/browser-native-os', '/docs/use-cases/business-automation', '/docs/use-cases/education', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('purpose', 'Why Settings matters in MapleOS', [
        'Settings is where system-level control becomes tangible. It gives MapleOS a place to express user choice, preferences, boundaries, and operating behavior in a way that feels consistent with an actual environment.',
        'That control layer is important because MapleOS is designed around guided intelligence rather than invisible automation.',
      ]),
      section('positioning', 'A control surface, not an afterthought', [
        'Many AI products treat control as secondary. MapleOS treats it as part of the core system. The Settings page shows that the platform expects people to shape the environment instead of passively accepting defaults.',
        'This makes Settings a concept bridge between the operating-system story and the human-in-control AI story.',
      ]),
      section('documentation-value', 'Why Settings deserves a dedicated docs page', [
        'A dedicated page helps search engines and AI systems understand that MapleOS includes explicit control surfaces. It also gives users a clear route when they want to understand how the platform is configured.',
        'That improves both usability and semantic clarity.',
      ]),
    ],
    faq: [
      faq('Why is Settings important for MapleOS positioning?', 'Because it reinforces that MapleOS is designed for user control, not only for AI output.'),
      faq('What concepts connect most strongly to Settings?', 'Human-in-control AI, browser-native operating systems, and AI operating-system design all connect strongly to Settings.'),
      faq('What should Settings pages link to?', 'They should link to core explainers, nearby operational surfaces, and use cases where control and workflow matter.'),
    ],
  }),
  withDefaults({
    id: 'docs-app-office',
    slug: 'office',
    path: '/docs/apps/office',
    title: 'Office App Docs',
    description: 'Documentation for Office, the MapleOS productivity surface for writing, documents, and everyday deliverables inside the AI operating environment.',
    summary: 'Office is the productivity surface in MapleOS for writing and document work. It keeps deliverables connected to context, knowledge, and related apps.',
    eyebrow: 'Docs / Apps',
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: 'Office',
    quickLinkPaths: ['/docs/use-cases/personal-productivity', '/docs/use-cases/content-creation', '/docs/apps/knowledge-base'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/ai-center', '/docs/concepts/knowledge-base', '/mapleos-for-consumers', '/docs/use-cases/education', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('role', 'What Office represents in MapleOS', [
        'Office gives MapleOS a practical productivity surface. It shows that the platform is not only for talking about work but also for producing actual deliverables inside the same environment.',
        'That matters for both users and search engines because it grounds the platform story in everyday use.',
      ]),
      section('connection', 'How Office connects to the wider system', [
        'Office works best when linked to Files, Knowledge Base, and use cases such as education and content creation. It is where collected context turns into written output.',
        'As a semantic node, Office helps connect consumer use cases to the broader MapleOS operating-system story.',
      ]),
      section('positioning', 'Why Office matters for audience fit', [
        'Office makes MapleOS easier to understand for consumers, students, creators, and professionals who need a direct path from research to writing.',
        'It reinforces the idea that MapleOS is a usable environment rather than a purely conceptual AI product.',
      ]),
    ],
    faq: [
      faq('Why include Office in the docs hub?', 'Because document work is one of the clearest everyday tasks MapleOS can support, and it helps make the product feel grounded and practical.'),
      faq('Does Office only matter for consumers?', 'No. Office also supports education, research summaries, team documentation, and structured professional output.'),
      faq('What concepts relate most to Office?', 'Knowledge organization, AI surfaces, and browser-native application workflows all connect strongly to Office.'),
    ],
  }),
  withDefaults({
    id: 'docs-app-games',
    slug: 'games',
    path: '/docs/apps/games',
    title: 'Games App Docs',
    description: 'Documentation for Games, the MapleOS entertainment surface that shows the app environment can support play as well as work.',
    summary: 'Games shows that MapleOS is a broader application environment. The platform can host entertainment surfaces alongside productive and AI-oriented workflows.',
    eyebrow: 'Docs / Apps',
    pageType: 'docs-app',
    categoryLabel: 'Apps',
    breadcrumbLabel: 'Games',
    quickLinkPaths: ['/docs/use-cases/gaming-and-entertainment', '/mapleos-for-consumers', '/what-is-mapleos'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/office', '/docs/concepts/browser-native-os', '/mapleos-vs-chromeos', '/ai-surfaces', '/docs/use-cases/personal-productivity'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('purpose', 'Why Games belongs in MapleOS docs', [
        'Games helps show that MapleOS is a real application environment rather than a narrow business tool. The presence of entertainment surfaces demonstrates that the app model can support more than strictly work-focused tasks.',
        'That broadness makes the operating-system framing more credible and more understandable.',
      ]),
      section('system-story', 'What Games says about the platform', [
        'An operating environment feels more complete when it can support multiple modes of use. Games adds that breadth while still fitting inside the same browser-native system structure as other MapleOS surfaces.',
        'The result is a more believable and human product story.',
      ]),
      section('audience-fit', 'Who this page helps', [
        'The Games page helps consumers, educators, and general web-computing audiences understand that MapleOS is not limited to one narrow productivity category.',
        'It is especially useful when positioning MapleOS against ChromeOS or when explaining the flexibility of its app environment.',
      ]),
    ],
    faq: [
      faq('Why include a Games page in a semantic SEO project?', 'Because it communicates that MapleOS has a broader app environment and is not only a work or chatbot product.'),
      faq('Does Games conflict with the professional MapleOS story?', 'No. It supports the broader operating-system story by showing the environment can host multiple kinds of applications.'),
      faq('What should Games link to?', 'It should link to consumer-facing pages, browser-native computing concepts, and relevant entertainment use cases.'),
    ],
  }),
];

export const docsConcepts = [
  withDefaults({
    id: 'docs-concept-ai-operating-system',
    slug: 'ai-operating-system',
    path: '/docs/concepts/ai-operating-system',
    title: 'AI Operating System Concept',
    description: 'A concept guide to the AI operating system idea in MapleOS, including system structure, user control, and the role of connected app surfaces.',
    summary: 'The AI operating system concept explains why MapleOS is better understood as a structured environment for intelligence rather than a single conversational endpoint.',
    eyebrow: 'Docs / Concepts',
    pageType: 'docs-concept',
    categoryLabel: 'Concepts',
    breadcrumbLabel: 'AI Operating System',
    quickLinkPaths: ['/ai-operating-system', '/docs/concepts/browser-native-os', '/docs/concepts/human-in-control-ai'],
    relatedLinkPaths: ['/docs/apps/ai-center', '/docs/apps/settings', '/docs/use-cases/software-development', '/docs/use-cases/business-automation', '/what-is-mapleos', '/mapleos-vs-chatgpt'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('definition', 'Defining the concept', [
        'An AI operating system is a system model where intelligence is embedded across surfaces, workflows, and application roles. MapleOS uses this concept to explain why the platform includes more than conversation.',
        'The concept matters because it creates a coherent language for describing the product to users, search engines, and AI systems.',
      ]),
      section('structure', 'Key properties of the concept', [
        'The concept includes named surfaces, stable routes, app-level purposes, and user-oriented controls. These elements turn broad AI capability into something navigable and easier to understand.',
        'That makes the concept a strong foundation for both product architecture and semantic content architecture.',
      ], {
        bullets: [
          'Multiple surfaces instead of one monolithic interface',
          'Named apps with understandable roles',
          'Links between concepts, apps, and outcomes',
          'A stronger place for human guidance and review',
        ],
      }),
      section('relationship', 'How the concept maps to MapleOS', [
        'MapleOS expresses the AI operating system idea through pages like AI Center, Files, Settings, Knowledge Base, and AI Blueprints. Together these pages show that AI lives inside a system instead of floating above it.',
        'That relationship makes the concept page an essential node in the semantic graph.',
      ]),
    ],
    faq: [
      faq('Why is this a concept page instead of an app page?', 'Because it explains the underlying idea that connects multiple MapleOS surfaces and cannot be reduced to one app.'),
      faq('What pages should this concept connect to?', 'It should connect to explainers, control surfaces, workflow pages, and use cases that show the concept in action.'),
      faq('How is this concept different from “AI assistant”?', 'An AI assistant is a tool. An AI operating system is a structured environment where multiple tools and surfaces work together.'),
    ],
  }),
  withDefaults({
    id: 'docs-concept-ai-surfaces',
    slug: 'ai-surfaces',
    path: '/docs/concepts/ai-surfaces',
    title: 'AI Surfaces Concept',
    description: 'A concept guide to AI surfaces in MapleOS, explaining how intelligence appears across interfaces instead of hiding behind a single prompt box.',
    summary: 'The AI surfaces concept explains how MapleOS gives intelligence visible places and roles inside the environment.',
    eyebrow: 'Docs / Concepts',
    pageType: 'docs-concept',
    categoryLabel: 'Concepts',
    breadcrumbLabel: 'AI Surfaces',
    quickLinkPaths: ['/ai-surfaces', '/docs/apps/ai-center', '/docs/apps/knowledge-base'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/settings', '/docs/concepts/ai-operating-system', '/docs/concepts/knowledge-base', '/docs/use-cases/research', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('concept', 'What the AI surfaces concept means', [
        'AI surfaces are the visible places where MapleOS exposes intelligence. Each surface gives users a clearer mental model of what the system is doing and why that interface exists.',
        'This is a foundational concept because it helps separate MapleOS from products that rely on a single generic AI entry point.',
      ]),
      section('examples', 'Examples inside MapleOS', [
        'AI Center is an orientation surface. Knowledge Base is a context surface. Settings is a control surface. AI Blueprints is a repeatability surface. These examples show how the concept spreads across the platform.',
        'The pages work together to make the idea concrete.',
      ]),
      section('value', 'Why the concept matters', [
        'The concept matters because it improves usability and discoverability at the same time. Users can find the right place for a task, and search engines can see how product behavior maps to stable semantic pages.',
        'That combination makes AI surfaces a strong bridge between UX design and SEO design.',
      ]),
    ],
    faq: [
      faq('Are AI surfaces just UI sections?', 'No. They are interfaces with a defined role in the system, not just visual groupings.'),
      faq('Why does MapleOS need this concept page?', 'Because the idea explains multiple app pages and helps the platform read as a coherent system.'),
      faq('What should this concept page link to?', 'It should link to nearby concept pages, app docs, and use cases where surfaces matter in practice.'),
    ],
  }),
  withDefaults({
    id: 'docs-concept-ai-blueprints',
    slug: 'ai-blueprints',
    path: '/docs/concepts/ai-blueprints',
    title: 'AI Blueprints Concept',
    description: 'A concept guide to AI Blueprints in MapleOS, explaining how repeatable workflow patterns become visible, reusable, and reviewable.',
    summary: 'The AI Blueprints concept explains how MapleOS turns repeatable work into a stable pattern that users can inspect and reuse.',
    eyebrow: 'Docs / Concepts',
    pageType: 'docs-concept',
    categoryLabel: 'Concepts',
    breadcrumbLabel: 'AI Blueprints',
    quickLinkPaths: ['/ai-blueprints', '/docs/apps/ai-blueprints', '/docs/concepts/ai-workflows'],
    relatedLinkPaths: ['/docs/apps/settings', '/docs/apps/ai-center', '/docs/use-cases/business-automation', '/docs/use-cases/content-creation', '/mapleos-for-developers', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('concept', 'The idea behind blueprints', [
        'AI Blueprints is a concept about repeatability with visibility. Instead of burying automation behind opaque commands, MapleOS frames it as a reusable structure that can be described and relaunched.',
        'That makes the concept valuable both to users and to AI systems trying to understand the product.',
      ]),
      section('difference', 'Why concept clarity matters', [
        'A concept page helps distinguish the idea of blueprint-driven work from the specific app surface where users interact with it. This keeps the information architecture clean and helps the linking graph stay strong.',
        'It also improves the platform story by showing how MapleOS thinks about workflows, not just features.',
      ]),
      section('relationship', 'Where this concept shows up', [
        'This concept connects directly to AI workflows, human-in-control AI, and business automation use cases. Those links help users understand where blueprint thinking becomes useful in practice.',
        'It is one of the clearest semantic bridges from product language to real operational value.',
      ]),
    ],
    faq: [
      faq('Why not explain blueprints only on the app page?', 'Because the concept spans more than the app. It shapes how MapleOS thinks about repeatable work across the whole system.'),
      faq('What makes blueprints different from generic automation?', 'Blueprints emphasize visibility, reusability, and human readability instead of hidden one-click automation.'),
      faq('Which use cases fit blueprints best?', 'Business automation, content systems, recurring research, and repeatable internal workflows are all strong fits.'),
    ],
  }),
  withDefaults({
    id: 'docs-concept-browser-native-os',
    slug: 'browser-native-os',
    path: '/docs/concepts/browser-native-os',
    title: 'Browser-Native OS Concept',
    description: 'A concept guide to browser-native operating systems and how MapleOS uses web delivery while still behaving like a system with apps, surfaces, and workflows.',
    summary: 'The browser-native OS concept explains how MapleOS can live on the web while still acting like an operating environment with real structure.',
    eyebrow: 'Docs / Concepts',
    pageType: 'docs-concept',
    categoryLabel: 'Concepts',
    breadcrumbLabel: 'Browser-Native OS',
    quickLinkPaths: ['/ai-operating-system', '/mapleos-vs-chromeos', '/docs/apps/files'],
    relatedLinkPaths: ['/docs/apps/settings', '/docs/concepts/ai-operating-system', '/docs/use-cases/education', '/docs/use-cases/personal-productivity', '/mapleos-for-consumers', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('definition', 'What browser-native means', [
        'Browser-native does not mean lightweight or simplistic. In MapleOS, it means the operating environment is delivered through web technologies while still presenting stable routes, app surfaces, and system-like structure.',
        'That combination makes MapleOS accessible while preserving a coherent environment model.',
      ]),
      section('advantage', 'Why the concept matters for MapleOS', [
        'The browser-native OS concept helps explain why MapleOS is not just a traditional desktop port and not just a website either. It sits in a middle category that uses the web as the runtime for a system-shaped experience.',
        'This is especially useful in comparisons against ChromeOS and traditional desktop operating systems.',
      ]),
      section('outcome', 'What the concept enables', [
        'A browser-native operating environment can be highly linkable, crawlable, and semantically organized. That supports discoverability, AI readability, and smoother transitions between explanation and use.',
        'It is one of the reasons this semantic content layer is such a good fit for MapleOS.',
      ]),
    ],
    faq: [
      faq('Does browser-native mean MapleOS is just a website?', 'No. Browser-native describes the delivery model, not the full product identity. MapleOS still behaves like a structured operating environment.'),
      faq('Why compare this concept to ChromeOS?', 'Because ChromeOS is a familiar browser-centric reference point, but MapleOS adds a stronger AI operating-system layer.'),
      faq('How does browser-native delivery help semantic SEO?', 'Stable routes and SSR pages are easier for crawlers and AI systems to understand than interfaces that rely on client-side interaction alone.'),
    ],
  }),
  withDefaults({
    id: 'docs-concept-human-in-control-ai',
    slug: 'human-in-control-ai',
    path: '/docs/concepts/human-in-control-ai',
    title: 'Human-in-Control AI Concept',
    description: 'A concept guide to the human-in-control AI philosophy in MapleOS, including visible surfaces, user choice, and workflow review.',
    summary: 'Human-in-control AI is the idea that intelligence should remain visible, guideable, and shaped by the user rather than hidden behind autonomous defaults.',
    eyebrow: 'Docs / Concepts',
    pageType: 'docs-concept',
    categoryLabel: 'Concepts',
    breadcrumbLabel: 'Human-in-Control AI',
    quickLinkPaths: ['/docs/apps/settings', '/ai-operating-system', '/docs/concepts/ai-workflows'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/knowledge-base', '/docs/use-cases/business-automation', '/docs/use-cases/software-development', '/what-is-mapleos', '/mapleos-vs-chatgpt'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('philosophy', 'What human-in-control AI means', [
        'Human-in-control AI means the user stays oriented and empowered. In MapleOS, this shows up through visible surfaces, stable workflows, explicit app roles, and control pages such as Settings.',
        'It is a direct response to the tendency of AI products to hide process behind generic answers and invisible automation.',
      ]),
      section('platform', 'How MapleOS expresses the concept', [
        'MapleOS expresses this concept through linked documentation and product surfaces. Knowledge Base makes context visible, Files keeps material inspectable, Settings supports control, and AI Blueprints keeps repeatable work reviewable.',
        'Together those pages show a consistent product philosophy rather than scattered features.',
      ]),
      section('impact', 'Why the concept matters', [
        'This concept matters for trust, but it also matters for clarity. A platform that puts people in control is easier to explain because its system boundaries are visible.',
        'That makes human-in-control AI important for both product behavior and semantic communication.',
      ]),
    ],
    faq: [
      faq('Is human-in-control AI the same as no automation?', 'No. MapleOS supports workflows and automation, but it frames them in ways users can understand, inspect, and shape.'),
      faq('Why is this concept central to MapleOS?', 'Because it explains why the platform invests in surfaces, controls, and linked workflow pages instead of a purely opaque assistant model.'),
      faq('Which apps best demonstrate this concept?', 'Settings, Files, Knowledge Base, and AI Blueprints all show different aspects of human-guided AI behavior.'),
    ],
  }),
  withDefaults({
    id: 'docs-concept-ai-workflows',
    slug: 'ai-workflows',
    path: '/docs/concepts/ai-workflows',
    title: 'AI Workflows Concept',
    description: 'A concept guide to AI workflows in MapleOS, showing how tasks move through surfaces, steps, and repeatable structures instead of staying in one chat thread.',
    summary: 'The AI workflows concept explains how MapleOS turns tasks into visible sequences that connect surfaces, steps, and outcomes.',
    eyebrow: 'Docs / Concepts',
    pageType: 'docs-concept',
    categoryLabel: 'Concepts',
    breadcrumbLabel: 'AI Workflows',
    quickLinkPaths: ['/ai-blueprints', '/docs/apps/ai-blueprints', '/docs/use-cases/ai-workflows'],
    relatedLinkPaths: ['/docs/concepts/human-in-control-ai', '/docs/concepts/knowledge-base', '/docs/use-cases/business-automation', '/docs/use-cases/research', '/mapleos-for-developers', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('concept', 'What an AI workflow is in MapleOS', [
        'An AI workflow is more than a prompt. In MapleOS, it is a visible sequence that can involve orientation, context gathering, drafting, review, and repeated execution through a recognizable system path.',
        'This concept helps explain how MapleOS transforms general intelligence into operational structure.',
      ]),
      section('why-visible', 'Why workflow visibility matters', [
        'Visible workflows are easier to repeat, easier to teach, and easier to trust. They let people understand how work gets done and where intervention makes sense.',
        'That is why workflow pages are central to the MapleOS semantic layer: they connect abstract capability to real patterns of work.',
      ]),
      section('connections', 'What AI workflows connect to', [
        'AI workflows connect strongly to Knowledge Base, AI Blueprints, human-in-control AI, and use-case pages such as business automation and research. Those relationships make the concept page a strong hub inside the docs graph.',
        'It is also a useful destination for developers and operators who want a system-level explanation of repeatable AI work.',
      ]),
    ],
    faq: [
      faq('How is an AI workflow different from a conversation?', 'A conversation may help a workflow, but a workflow includes steps, surfaces, and outcomes that extend beyond a single thread.'),
      faq('Why is this concept important to MapleOS?', 'Because MapleOS is designed to support structured work, not only ad hoc questioning.'),
      faq('Which pages should link here?', 'Blueprint pages, use-case pages, developer pages, and control-oriented concept pages should all link to AI workflows.'),
    ],
  }),
  withDefaults({
    id: 'docs-concept-knowledge-base',
    slug: 'knowledge-base',
    path: '/docs/concepts/knowledge-base',
    title: 'Knowledge Base Concept',
    description: 'A concept guide to knowledge bases in MapleOS, including grounded context, reusable source material, and durable information organization.',
    summary: 'The Knowledge Base concept explains how MapleOS treats context as something people can organize, review, and reuse across workflows.',
    eyebrow: 'Docs / Concepts',
    pageType: 'docs-concept',
    categoryLabel: 'Concepts',
    breadcrumbLabel: 'Knowledge Base',
    quickLinkPaths: ['/docs/apps/knowledge-base', '/docs/use-cases/research', '/docs/use-cases/education'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/office', '/docs/concepts/ai-surfaces', '/docs/concepts/ai-workflows', '/mapleos-for-developers', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'faq'],
    sections: [
      section('meaning', 'What a knowledge base means in MapleOS', [
        'A knowledge base is a durable context system. In MapleOS, it refers to organized source material that can support grounded work instead of leaving AI interactions disconnected from evidence and references.',
        'That concept helps explain why MapleOS invests in context surfaces rather than relying only on ephemeral chat history.',
      ]),
      section('benefit', 'Why the concept matters', [
        'Knowledge systems make research, writing, education, and development more reliable because source material is easier to revisit and connect to outputs. This is a foundational part of MapleOS as an application environment.',
        'The concept also helps search engines and AI systems understand that MapleOS has a real information architecture around knowledge.',
      ]),
      section('relationships', 'How the concept connects to other pages', [
        'Knowledge Base links naturally to Files, Office, AI workflows, research, education, and software development. Those relationships make it one of the best pages for internal linking and semantic consistency.',
        'It is also a key concept for anyone evaluating MapleOS beyond simple prompt-based use.',
      ]),
    ],
    faq: [
      faq('Why is Knowledge Base a concept as well as an app?', 'Because the idea of organized, grounded context influences multiple parts of the platform and cannot be reduced to one interface.'),
      faq('Does a knowledge base only matter for research?', 'No. It also matters for development, writing, education, and any task that depends on reusable source material.'),
      faq('How does this concept support MapleOS positioning?', 'It reinforces that MapleOS is an environment for context-rich work rather than a shallow chat product.'),
    ],
  }),
];

export const docsUseCases = [
  withDefaults({
    id: 'docs-use-case-personal-productivity',
    slug: 'personal-productivity',
    path: '/docs/use-cases/personal-productivity',
    title: 'Personal Productivity with MapleOS',
    description: 'See how MapleOS supports personal productivity through files, office tools, AI surfaces, and browser-native workflow structure.',
    summary: 'MapleOS supports personal productivity by keeping writing, files, AI assistance, and organization inside one browser-native environment.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Personal Productivity',
    quickLinkPaths: ['/mapleos-for-consumers', '/docs/apps/office', '/docs/apps/files'],
    relatedLinkPaths: ['/docs/apps/knowledge-base', '/docs/apps/ai-center', '/docs/concepts/ai-surfaces', '/docs/use-cases/content-creation', '/mapleos-vs-chromeos', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('fit', 'Why MapleOS fits personal productivity', [
        'Personal productivity works best when tasks, files, and context do not scatter across disconnected tools. MapleOS addresses that by placing office work, files, AI orientation, and knowledge in one environment.',
        'This is especially helpful for people who want a calmer browser-native workspace with stronger structure.',
      ]),
      section('how', 'A simple MapleOS productivity flow', [
        'A typical flow starts in AI Center, moves through Files or Knowledge Base, and ends in Office or another production surface. The point is not that every task needs AI, but that AI can participate without taking over the workflow.',
        'That model supports planning, note-taking, studying, personal writing, and routine organization.',
      ], {
        steps: [
          'Open AI Center to orient around the current task.',
          'Gather or review materials in Files or Knowledge Base.',
          'Produce or revise output in Office.',
          'Return to related concept or use-case pages when refining the workflow.',
        ],
      }),
      section('benefit', 'Why this use case matters semantically', [
        'Personal productivity is one of the clearest audience-level applications for MapleOS. It helps search engines and AI systems understand that the platform can support everyday work without being reduced to a generic AI tool.',
        'It also creates a clean bridge between consumer pages and app documentation.',
      ]),
    ],
    faq: [
      faq('Is MapleOS only for advanced users?', 'No. Personal productivity is one of the simplest and clearest fits for MapleOS.'),
      faq('Which apps matter most for personal productivity?', 'Office, Files, Knowledge Base, and AI Center are the most direct supporting surfaces.'),
      faq('Why include workflow steps on this page?', 'They make the use case more concrete and provide structured data for systems that parse practical task flows.'),
    ],
  }),
  withDefaults({
    id: 'docs-use-case-ai-workflows',
    slug: 'ai-workflows',
    path: '/docs/use-cases/ai-workflows',
    title: 'AI Workflows with MapleOS',
    description: 'See how MapleOS supports AI workflows through visible surfaces, knowledge organization, repeatable blueprints, and human-guided execution.',
    summary: 'MapleOS supports AI workflows by turning tasks into linked, visible system paths rather than isolated prompt sessions.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'AI Workflows',
    quickLinkPaths: ['/docs/concepts/ai-workflows', '/docs/apps/ai-blueprints', '/docs/concepts/human-in-control-ai'],
    relatedLinkPaths: ['/docs/apps/knowledge-base', '/docs/apps/settings', '/docs/use-cases/business-automation', '/docs/use-cases/software-development', '/ai-blueprints', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('use-case', 'Why MapleOS is suited to AI workflows', [
        'AI workflows need more structure than a generic chatbot usually provides. MapleOS helps by offering orientation surfaces, context surfaces, control surfaces, and repeatability surfaces that can be linked together.',
        'That makes workflow design and execution easier to explain and easier to repeat.',
      ]),
      section('process', 'A standard MapleOS workflow pattern', [
        'A strong workflow often starts by defining the goal, gathering source material, deciding which surfaces are relevant, and then using blueprint-style structures for repeated execution. Each stage has a natural place inside MapleOS.',
        'This creates a more legible system than jumping from prompt to prompt without a stable environment.',
      ], {
        steps: [
          'Clarify the goal and choose the right app surface.',
          'Organize source material in Knowledge Base or Files.',
          'Design or select a repeatable pattern through AI Blueprints.',
          'Review and refine the workflow with human oversight.',
        ],
      }),
      section('value', 'Why the use case matters', [
        'This page helps connect broad platform language to practical operational value. It is useful for builders, operators, and teams evaluating MapleOS as a workflow environment.',
        'It also reinforces the difference between MapleOS and simpler chat-only products.',
      ]),
    ],
    faq: [
      faq('What is the difference between the AI workflow concept and this use case page?', 'The concept page explains the idea; this page explains how to apply it to real work.'),
      faq('Which app is most important for AI workflows?', 'AI Blueprints is the most directly relevant app, but Knowledge Base, Files, and Settings also matter.'),
      faq('Why does this page use HowTo-style structure?', 'Because AI workflows are best understood as repeatable sequences, not only as abstract descriptions.'),
    ],
  }),
  withDefaults({
    id: 'docs-use-case-research',
    slug: 'research',
    path: '/docs/use-cases/research',
    title: 'Research with MapleOS',
    description: 'See how MapleOS supports research through Knowledge Base, Files, AI surfaces, and browser-native workflows that connect source material to output.',
    summary: 'MapleOS supports research by giving source material, notes, AI orientation, and output surfaces a coherent place inside one environment.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Research',
    quickLinkPaths: ['/docs/apps/knowledge-base', '/docs/concepts/knowledge-base', '/ai-center'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/office', '/docs/use-cases/education', '/docs/use-cases/content-creation', '/docs/concepts/ai-surfaces', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('fit', 'Why research is a natural MapleOS use case', [
        'Research depends on finding, organizing, comparing, and synthesizing source material. MapleOS supports that by connecting Knowledge Base, Files, Office, and AI surfaces inside one browser-native environment.',
        'This reduces the fragmentation that often makes research feel slower than it needs to be.',
      ]),
      section('process', 'A research flow in MapleOS', [
        'A common research path begins with orienting around a topic, gathering source material, organizing references, and then producing summaries or drafts. MapleOS lets those steps live close together instead of across unrelated tools.',
        'That is useful for students, analysts, builders, and creators alike.',
      ], {
        steps: [
          'Use AI Center to frame the research task.',
          'Store references and notes in Knowledge Base or Files.',
          'Compare material and distill the key ideas.',
          'Turn findings into documents or reusable workflows.',
        ],
      }),
      section('value', 'Why this use case matters', [
        'Research is one of the best demonstrations of MapleOS as a system for grounded work. It connects knowledge, interfaces, documents, and repeatable processes in a way that is easy to explain semantically.',
        'It also provides strong internal linking opportunities across app, concept, and audience pages.',
      ]),
    ],
    faq: [
      faq('Which MapleOS app matters most for research?', 'Knowledge Base is the strongest anchor, but Files, Office, and AI Center all play important roles.'),
      faq('Why is research a strong MapleOS example?', 'Because it highlights context handling, organization, and the value of multiple linked surfaces.'),
      faq('Does MapleOS replace specialist research tools?', 'Not necessarily. It is better understood as a browser-native environment that can support and organize research workflows.'),
    ],
  }),
  withDefaults({
    id: 'docs-use-case-software-development',
    slug: 'software-development',
    path: '/docs/use-cases/software-development',
    title: 'Software Development with MapleOS',
    description: 'See how MapleOS supports software development through knowledge handling, workflow structure, AI surfaces, and a browser-native operating environment.',
    summary: 'MapleOS supports software development by giving developers a structured environment for knowledge, workflow, and AI-guided work instead of a single code-adjacent chat tab.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Software Development',
    quickLinkPaths: ['/mapleos-for-developers', '/docs/concepts/ai-workflows', '/docs/apps/knowledge-base'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/settings', '/docs/use-cases/research', '/docs/use-cases/business-automation', '/mapleos-vs-chatgpt', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('fit', 'Why software development fits MapleOS', [
        'Software development often requires moving between documentation, code context, planning, reusable workflows, and review. MapleOS supports that by linking concepts, app surfaces, and use-case pages into a coherent system.',
        'This makes it a better semantic match for durable development work than a generic assistant alone.',
      ]),
      section('process', 'A development-oriented MapleOS flow', [
        'Developers can use Knowledge Base for reference material, Files for project assets, Settings for control, and AI workflow pages for repeated patterns such as analysis, planning, or content generation around software projects.',
        'The goal is not to collapse development into one AI feature, but to embed AI into a clearer working environment.',
      ], {
        steps: [
          'Orient around the development goal and collect relevant context.',
          'Use Knowledge Base and Files to organize reference material.',
          'Apply AI workflow patterns or blueprint-style repeatable steps.',
          'Refine documentation, plans, or outputs inside linked product surfaces.',
        ],
      }),
      section('value', 'Why this page matters for MapleOS', [
        'Software development is a strong audience fit because it shows the value of structured AI work. It also gives developer audiences a practical lens for understanding the platform architecture.',
        'That makes it a high-value page in the semantic content graph.',
      ]),
    ],
    faq: [
      faq('Is MapleOS a coding IDE?', 'Not exactly. MapleOS is better described as a browser-native AI operating environment that can support software development workflows.'),
      faq('Why link software development to knowledge systems?', 'Because durable development depends on documentation, references, and repeatable process as much as direct code generation.'),
      faq('How is this different from coding with a chatbot?', 'MapleOS frames development as part of a broader environment with linked surfaces, context handling, and workflow semantics.'),
    ],
  }),
  withDefaults({
    id: 'docs-use-case-content-creation',
    slug: 'content-creation',
    path: '/docs/use-cases/content-creation',
    title: 'Content Creation with MapleOS',
    description: 'See how MapleOS supports content creation through research, knowledge organization, office work, and repeatable AI-assisted workflows.',
    summary: 'MapleOS supports content creation by linking ideation, source gathering, drafting, and repeatable production workflows inside one environment.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Content Creation',
    quickLinkPaths: ['/docs/apps/office', '/docs/apps/knowledge-base', '/docs/apps/ai-blueprints'],
    relatedLinkPaths: ['/mapleos-for-consumers', '/docs/use-cases/research', '/docs/use-cases/personal-productivity', '/docs/concepts/ai-workflows', '/ai-blueprints', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('fit', 'Why MapleOS fits content creation', [
        'Content creation usually moves through research, idea development, drafting, editing, and reuse. MapleOS supports that sequence through Knowledge Base, Office, AI Center, and blueprint-oriented workflow thinking.',
        'This makes the platform useful for people who want creative work to stay organized instead of fragmented across tabs and tools.',
      ]),
      section('process', 'A MapleOS content workflow', [
        'Creators can gather source material, develop themes, move into drafting, and then turn successful patterns into repeatable flows. The semantic docs structure mirrors that process by linking concept pages, app pages, and outcome pages together.',
        'That alignment makes the product easier to describe and easier to use.',
      ], {
        steps: [
          'Collect source material and references.',
          'Shape the narrative or content plan inside MapleOS.',
          'Draft and revise in Office or related surfaces.',
          'Reuse successful creation patterns with blueprint-style workflows.',
        ],
      }),
      section('value', 'Why this use case matters', [
        'Content creation helps show that MapleOS can support practical, ongoing work rather than only abstract AI experimentation. It also provides a natural bridge between consumer and workflow-oriented pages.',
        'That makes it valuable for SEO and for audience clarity.',
      ]),
    ],
    faq: [
      faq('Is MapleOS only for technical content creators?', 'No. The content creation use case also applies to writers, marketers, educators, and creators who need a structured workspace.'),
      faq('Which apps matter most for creators?', 'Knowledge Base, Office, AI Center, and AI Blueprints are especially relevant.'),
      faq('Why does content creation connect well to blueprints?', 'Because successful creative processes are often repeatable, and blueprints make that repeatability easier to preserve.'),
    ],
  }),
  withDefaults({
    id: 'docs-use-case-business-automation',
    slug: 'business-automation',
    path: '/docs/use-cases/business-automation',
    title: 'Business Automation with MapleOS',
    description: 'See how MapleOS supports business automation through visible workflows, AI Blueprints, knowledge organization, and user-controlled system behavior.',
    summary: 'MapleOS supports business automation by making repeatable work visible, documented, and easier to relaunch through structured surfaces.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Business Automation',
    quickLinkPaths: ['/docs/apps/ai-blueprints', '/docs/concepts/ai-workflows', '/docs/concepts/human-in-control-ai'],
    relatedLinkPaths: ['/docs/apps/settings', '/docs/apps/knowledge-base', '/mapleos-for-developers', '/ai-blueprints', '/docs/use-cases/software-development', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('fit', 'Why MapleOS fits business automation', [
        'Business automation works best when repeated work can be explained, revised, and monitored. MapleOS supports that by linking blueprints, workflows, knowledge, and control surfaces into a system that stays understandable.',
        'That makes automation feel less like magic and more like an operational asset.',
      ]),
      section('process', 'A typical business automation flow', [
        'A business team can define a recurring process, gather the right context, map the steps, and then relaunch the pattern through AI Blueprints or related workflow surfaces. Because the environment is semantically structured, it is easier to document and govern.',
        'This is a good example of MapleOS as an application environment rather than a single AI endpoint.',
      ], {
        steps: [
          'Define the repeated task and what success looks like.',
          'Organize source material and operational context.',
          'Model the repeatable pattern through blueprint-oriented workflow thinking.',
          'Run, review, and improve the process over time.',
        ],
      }),
      section('value', 'Why this use case matters', [
        'Business automation is one of the clearest demonstrations of MapleOS workflow value. It connects concept pages, app pages, and audience pages in a way that search engines and AI systems can interpret clearly.',
        'It also reinforces the platform’s balanced positioning: capable, but still grounded and human-guided.',
      ]),
    ],
    faq: [
      faq('Does MapleOS promise full autonomous business operation?', 'No. MapleOS should be described as supporting visible, user-guided automation and repeatable workflows rather than replacing human judgment entirely.'),
      faq('Why is business automation linked to Settings?', 'Because control and system behavior are central when automation affects real operational work.'),
      faq('What makes MapleOS different from automation-only tools?', 'It combines workflow thinking with an operating-environment model, giving automation a clearer place inside a broader system.'),
    ],
  }),
  withDefaults({
    id: 'docs-use-case-education',
    slug: 'education',
    path: '/docs/use-cases/education',
    title: 'Education with MapleOS',
    description: 'See how MapleOS supports education through research, knowledge bases, office tools, browser-native delivery, and structured AI guidance.',
    summary: 'MapleOS supports education by combining research, knowledge organization, document work, and AI guidance inside a browser-native environment.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Education',
    quickLinkPaths: ['/docs/use-cases/research', '/docs/apps/office', '/mapleos-vs-chromeos'],
    relatedLinkPaths: ['/docs/apps/knowledge-base', '/docs/apps/files', '/docs/concepts/browser-native-os', '/mapleos-for-consumers', '/docs/use-cases/personal-productivity', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('fit', 'Why MapleOS fits education', [
        'Education depends on reading, organizing, summarizing, and producing work from source material. MapleOS supports those tasks through linked app surfaces and a browser-native operating model that is easier to access than heavy local setups.',
        'That makes it a strong fit for students, self-learners, and education-oriented creators.',
      ]),
      section('process', 'A simple learning workflow', [
        'A student can gather reference material, store it in Knowledge Base, work through it in Files, and turn it into notes or output in Office. AI surfaces can help orient and summarize without becoming the only interface in the system.',
        'This supports both independent study and structured classroom workflows.',
      ], {
        steps: [
          'Collect course material or references.',
          'Organize the material in Knowledge Base or Files.',
          'Use MapleOS surfaces to summarize, compare, and plan.',
          'Draft learning outputs, notes, or presentations in Office.',
        ],
      }),
      section('value', 'Why the education page matters', [
        'Education is a strong semantic use case because it bridges consumer accessibility, browser-native delivery, and grounded knowledge handling. It also makes the ChromeOS comparison more understandable.',
        'That gives MapleOS another clear audience fit beyond technical users.',
      ]),
    ],
    faq: [
      faq('Why is MapleOS relevant to education?', 'Because it provides a structured, browser-native environment for research, note-taking, document work, and AI-guided study flows.'),
      faq('Does MapleOS target only schools?', 'No. Education includes self-learning, tutoring, training, and independent study as well as institutional settings.'),
      faq('Which comparison page is most relevant here?', 'MapleOS vs ChromeOS is especially relevant because it helps explain browser-native accessibility and everyday computing fit.'),
    ],
  }),
  withDefaults({
    id: 'docs-use-case-gaming-and-entertainment',
    slug: 'gaming-and-entertainment',
    path: '/docs/use-cases/gaming-and-entertainment',
    title: 'Gaming and Entertainment with MapleOS',
    description: 'See how MapleOS supports gaming and entertainment as part of a broader browser-native application environment, not only a work-focused AI system.',
    summary: 'MapleOS supports gaming and entertainment to show that the platform is a broader app environment with room for play alongside work.',
    eyebrow: 'Docs / Use Cases',
    pageType: 'docs-use-case',
    categoryLabel: 'Use Cases',
    breadcrumbLabel: 'Gaming and Entertainment',
    quickLinkPaths: ['/docs/apps/games', '/mapleos-for-consumers', '/mapleos-vs-chromeos'],
    relatedLinkPaths: ['/docs/apps/files', '/docs/apps/office', '/docs/concepts/browser-native-os', '/docs/use-cases/personal-productivity', '/ai-surfaces', '/what-is-mapleos'],
    schemaHints: ['techArticle', 'howTo', 'faq'],
    sections: [
      section('fit', 'Why entertainment belongs in the MapleOS story', [
        'Entertainment shows that MapleOS is a true application environment, not only an AI productivity shell. The platform can support games and leisure-oriented apps while still keeping a coherent operating-system identity.',
        'That makes the product story broader and more believable.',
      ]),
      section('process', 'How the use case fits the system', [
        'Gaming and entertainment can coexist with files, consumer workflows, and browser-native interfaces inside MapleOS. This demonstrates that the app model is flexible enough to host multiple modes of use.',
        'It also gives consumer audiences a more complete picture of the platform.',
      ], {
        steps: [
          'Use the Docs hub or app pages to discover entertainment surfaces.',
          'Launch relevant apps inside the MapleOS environment.',
          'Move between play and adjacent files or consumer workflows as needed.',
          'Use related pages to understand the broader app model.',
        ],
      }),
      section('value', 'Why this use case matters', [
        'This page helps communicate breadth. MapleOS is not just a chatbot, not just a document tool, and not just a Windows comparison. It is an environment capable of hosting different kinds of applications.',
        'That breadth supports the operating-system framing in a grounded way.',
      ]),
    ],
    faq: [
      faq('Why include gaming in a semantic SEO layer?', 'Because it helps show that MapleOS is a broader application environment with multiple classes of experience.'),
      faq('Does this weaken the productivity story?', 'No. It strengthens the operating-system story by demonstrating that MapleOS is flexible and not locked to one narrow function.'),
      faq('Which audience page relates most strongly?', 'The consumer audience page is the closest match, though the browser-native OS concept is also relevant.'),
    ],
  }),
];

export const allSemanticPages = [
  ...corePages,
  ...comparisonPages,
  ...docsApps,
  ...docsConcepts,
  ...docsUseCases,
];
